import { RootState } from "@app/store"
import { Role } from "@features/login/loginSlice"
import ProgramsMenu from "../../programs/menu/programsMenu"
import { SyllabusMainPage } from "../../main/syllabuses-main-page/syllabusesMainPage"
import { CoursesMainPage } from "../../main/coursesMainPage"
import { SalesManagement } from "../../sales-management/salesManagement"
import { SyllabusViewContainer } from "../../view/syllabusViewContainer"
import { CourseViewContainer } from "../../editor/courseViewContainer"
import { SyllabusEditorContainer } from "../../editor/syllabus/syllabusEditor"
import { CourseEditorContainer } from "../../editor/courseEditor"
import { ProjectsTabView } from "../../projects/projectsTabView"
import ProgramViewContainer from "../../programs/program/components/programContainer"
import { DeeplinksView } from "../../main/deeplinks/deeplinksView"
import CalendarContainer from "../../main/calendar/CalendarContainer"
import AppFeatureFlags from "@features/app-feature-flags/appFeatureFlags"
import AccountManagement from "../../account-management/accountManagement"
import { ShiftManagement } from "../../sales-management/shiftsManagement"
import { createSelector } from "@reduxjs/toolkit"
import { selectLoggedInUser } from "@features/login/loginSelectors"
import { UsersTab } from "../../tab-views/users-tab/usersTab"
import { BookSessionWithTutor } from "@features/bookSessionWithTutor/bookSessionWithTutor"
import { StudentsTabView } from "../../tab-views/students-tab/studentsTabView"
import { TracksMainPage } from "../../main/tracks/tracksMainPage"
import { TrackPage } from "../../main/tracks/trackPage"
import CourseMajorsContainer from "../../main/courseMajorsContainer"
import { CourseInstanceMainPage } from "../../course-instance/courseInstanceMainPage"
import { CourseInstanceManagerMainPage } from "../../course-instance/courseInstanceManagerMainPage"
import { CourseInstanceRedirect } from "../../course-instance/courseInstanceRedirect"
import { LeadRoutingPage } from "../../lead-routing/leadRoutingPage"

export const enum TabIdentifier {
  Syllabuses = "Syllabuses",
  CourseManager = "CourseManager",
  CourseBuilder = "CourseBuilder",
  Tracks = "Tracks",
  Programs = "Programs",
  Projects = "Projects",
  Deeplinks = "Deeplinks",
  AppFeatureFlags = "AppFeatureFlags",
  Calendar = "Calendar",
  SalesManagement = "SalesManagement",
  LeadRouting = "LeadRouting",
  Users = "Users",
  BookSession = "BookSession",
  AccountManagement = "AccountManagement",
  Students = "Students",
}

export const tabIdentifierToTitle: Record<TabIdentifier, string> = {
  Programs: "Programs",
  Syllabuses: "Syllabuses",
  CourseBuilder: "Course Builder",
  CourseManager: "Courses",
  Tracks: "Tracks",
  Projects: "Projects",
  AccountManagement: "Employees & Faculty",
  Deeplinks: "Deep Links",
  AppFeatureFlags: "App Feature Flags",
  Calendar: "Calendar",
  SalesManagement: "Manage Consultants",
  LeadRouting: "Lead Routing",
  Users: "Users",
  BookSession: "Book a session",
  Students: "Students",
}

export const tabIdentifierToRouteDescriptor: Record<
  TabIdentifier,
  {
    path: string
    hideMainSideBar: boolean
    element: JSX.Element
    beta?: boolean
  }[]
> = {
  Programs: [
    { path: "programs", hideMainSideBar: false, element: <ProgramsMenu /> },
    {
      path: "program/:programId",
      hideMainSideBar: true,
      element: <ProgramViewContainer />,
    },
    {
      path: "program/:programId/:classId",
      hideMainSideBar: true,
      element: <ProgramViewContainer />,
    },
  ],
  CourseManager: [
    {
      path: "course-manager",
      hideMainSideBar: false,
      element: <CourseInstanceManagerMainPage />,
    },
    {
      path: "course-manager/course/:courseId",
      hideMainSideBar: true,
      element: <CourseInstanceRedirect />,
    },
    {
      path: "course-manager/course-instance/:courseInstanceId",
      hideMainSideBar: true,
      element: <CourseInstanceMainPage />,
    },
  ],
  Students: [
    {
      path: "students",
      hideMainSideBar: false,
      element: <StudentsTabView />,
    },
  ],
  Syllabuses: [
    {
      path: "syllabuses",
      hideMainSideBar: false,
      element: <SyllabusMainPage />,
    },
    {
      path: "syllabus/:syllabusId",
      hideMainSideBar: true,
      element: <SyllabusViewContainer />,
    },
    {
      path: "syllabus-edit/:syllabusId",
      hideMainSideBar: true,
      element: <SyllabusEditorContainer />,
    },
    {
      path: "syllabus-edit/:syllabusId/:unitId/:courseId",
      hideMainSideBar: true,
      element: <SyllabusEditorContainer />,
    },
  ],
  CourseBuilder: [
    { path: "courses", hideMainSideBar: false, element: <CoursesMainPage /> },
    {
      path: "course-versions/:courseId",
      hideMainSideBar: false,
      element: <CourseMajorsContainer />,
    },
    {
      path: "course/:courseId",
      hideMainSideBar: true,
      element: <CourseViewContainer />,
    },
    {
      path: "course-edit/:courseId",
      hideMainSideBar: true,
      element: <CourseEditorContainer />,
    },
  ],
  Tracks: [
    { path: "tracks", hideMainSideBar: false, element: <TracksMainPage /> },
    {
      path: "track/:trackId",
      hideMainSideBar: true,
      element: <TrackPage />,
    },
  ],
  Projects: [
    { path: "projects", hideMainSideBar: false, element: <ProjectsTabView /> },
  ],
  AccountManagement: [
    {
      path: "account-management",
      hideMainSideBar: false,
      element: <AccountManagement />,
    },
  ],
  Deeplinks: [
    { path: "deep-links", hideMainSideBar: false, element: <DeeplinksView /> },
  ],
  AppFeatureFlags: [
    {
      path: "app-feature-flags",
      hideMainSideBar: false,
      element: <AppFeatureFlags />,
    },
  ],
  Calendar: [
    {
      path: "calendar",
      hideMainSideBar: false,
      element: <CalendarContainer />,
    },
  ],
  BookSession: [
    {
      path: "book-a-session",
      hideMainSideBar: false,
      element: <BookSessionWithTutor />,
    },
  ],
  SalesManagement: [
    {
      path: "sales-management",
      hideMainSideBar: false,
      element: <SalesManagement />,
    },
    {
      path: "sales-management/shifts/:consultantId",
      hideMainSideBar: false,
      element: <ShiftManagement />,
    },
  ],
  LeadRouting: [
    {
      path: "lead-routing",
      hideMainSideBar: false,
      element: <LeadRoutingPage />,
    },
  ],
  Users: [
    {
      path: "users",
      hideMainSideBar: false,
      element: <UsersTab />,
      beta: true,
    },
  ],
}

const rolesToTabs: Record<Role, TabIdentifier[]> = {
  program_manager: [
    TabIdentifier.Syllabuses,
    TabIdentifier.CourseBuilder,
    TabIdentifier.Projects,
    TabIdentifier.Programs,
    TabIdentifier.Deeplinks,
    TabIdentifier.Calendar,
    TabIdentifier.Students,
    TabIdentifier.Tracks,
    TabIdentifier.CourseManager,
    TabIdentifier.Users,
  ],
  ms_employee: [
    TabIdentifier.Syllabuses,
    TabIdentifier.CourseBuilder,
    TabIdentifier.Tracks,
    TabIdentifier.CourseManager,
    TabIdentifier.Projects,
    TabIdentifier.Calendar,
  ],
  instructor: [
    TabIdentifier.Syllabuses,
    TabIdentifier.CourseBuilder,
    TabIdentifier.Projects,
    TabIdentifier.Calendar,
  ],
  account_manager: [
    TabIdentifier.Syllabuses,
    TabIdentifier.CourseBuilder,
    TabIdentifier.Projects,
    TabIdentifier.AccountManagement,
    TabIdentifier.Users,
    TabIdentifier.Calendar,
    TabIdentifier.Students,
    TabIdentifier.Tracks,
    TabIdentifier.CourseManager,
  ],
  lms_admin: [], // all tabs
  school_master_admin: [
    TabIdentifier.Syllabuses,
    TabIdentifier.CourseBuilder,
    TabIdentifier.Projects,
    TabIdentifier.Calendar,
  ],
  career_success_advisor: [
    TabIdentifier.Syllabuses,
    TabIdentifier.CourseBuilder,
    TabIdentifier.Projects,
    TabIdentifier.Programs,
    TabIdentifier.Calendar,
    TabIdentifier.Students,
    TabIdentifier.Users,
  ],
  mentor: [
    TabIdentifier.Syllabuses,
    TabIdentifier.CourseBuilder,
    TabIdentifier.Projects,
    TabIdentifier.Calendar,
    TabIdentifier.Users,
  ],
  professional_mentor: [
    TabIdentifier.Syllabuses,
    TabIdentifier.CourseBuilder,
    TabIdentifier.Projects,
    TabIdentifier.Calendar,
  ],
  sales_team_lead: [TabIdentifier.SalesManagement, TabIdentifier.Calendar],
  sales_admin: [
    TabIdentifier.SalesManagement,
    TabIdentifier.LeadRouting,
    TabIdentifier.Calendar,
  ],
  academic_support_advisor: [TabIdentifier.BookSession, TabIdentifier.Calendar],
  tutor: [TabIdentifier.Students],
  course_manager: [TabIdentifier.CourseManager],
  career_consultant: [TabIdentifier.LeadRouting],
}

const TABS_ORDER = [
  TabIdentifier.Students,
  TabIdentifier.CourseManager,
  TabIdentifier.Programs,
  TabIdentifier.Syllabuses,
  TabIdentifier.CourseBuilder,
  TabIdentifier.Tracks,
  TabIdentifier.Projects,
  TabIdentifier.Calendar,
  TabIdentifier.Deeplinks,
  TabIdentifier.AppFeatureFlags,
  TabIdentifier.SalesManagement,
  TabIdentifier.LeadRouting,
  TabIdentifier.BookSession,
  TabIdentifier.AccountManagement,
  TabIdentifier.Users,
]

export const TABS_GROUPS = [
  {
    title: "School manager",
    tabs: [
      TabIdentifier.Students,
      TabIdentifier.CourseManager,
      TabIdentifier.Programs,
      TabIdentifier.Calendar,
      TabIdentifier.BookSession,
    ],
  },
  {
    title: "Content",
    tabs: [
      TabIdentifier.CourseBuilder,
      TabIdentifier.Tracks,
      TabIdentifier.Projects,
      TabIdentifier.Syllabuses,
    ],
  },
  {
    title: "Career Consultants",
    tabs: [TabIdentifier.SalesManagement, TabIdentifier.LeadRouting],
  },
  {
    title: "Backoffice",
    tabs: [
      TabIdentifier.AccountManagement,
      TabIdentifier.Deeplinks,
      TabIdentifier.AppFeatureFlags,
      TabIdentifier.Users,
    ],
  },
]

const selectRoles = (state: RootState) => {
  const user = selectLoggedInUser(state)
  if (!user) {
    return "unknown"
  }
  return user.roles
}

export const tabsSelector = createSelector([selectRoles], (roles) => {
  if (roles === "unknown") {
    return "unknown"
  }
  const tabs: TabIdentifier[] = []

  const tabsForRole = roles.flatMap((role) => rolesToTabs[role])

  return TABS_ORDER.filter((tab) => {
    // Lead routing is gated to its own roles (sales_admin for the admin surface,
    // career_consultant for the personal "My routing" view) — it is never
    // granted by the lms_admin all-tabs bypass.
    if (tab === TabIdentifier.LeadRouting) {
      return tabsForRole.includes(tab)
    }
    return (
      roles.includes(Role.LmsAdmin) ||
      tabsForRole.includes(tab) ||
      tabs.includes(tab)
    )
  })
})

export const tabGroupsSelector = createSelector(
  [tabsSelector],
  (visibleTabsForUser) => {
    return TABS_GROUPS.map((group) => ({
      title: group.title,
      tabs: group.tabs.filter((tab) => visibleTabsForUser.includes(tab)),
    })).filter((group) => group.tabs.length > 0)
  },
)

export const routesSelector = createSelector([tabsSelector], (orderedTabs) => {
  if (orderedTabs === "unknown") {
    return "unknown"
  }

  return orderedTabs.flatMap((tab) => tabIdentifierToRouteDescriptor[tab])
})
